import React from 'react';
import { useLanguage } from '../i18n';
import { PRODUCTS, getProduct, productUrl } from '../products';

/**
 * More from TamerinTECH: MBot as the live product with its classroom photos,
 * the other projects with their statuses and associated institutions.
 */
const WorkSection: React.FC = () => {
  const { t, language } = useLanguage();
  const mbot = getProduct('mbot');
  const mbotCopy = t.products.items.mbot;
  const mbotHref = productUrl(mbot, language, 'homepage_featured') ?? '#';
  const others = PRODUCTS.filter((product) => !product.featured && product.id !== 'mbot');

  return (
    <section className="lp-work" id="work" aria-labelledby="work-heading">
      <div className="lp-wrap">
        <div className="lp-work-heading">
          <h2 id="work-heading">{t.work.heading}</h2>
          <p>{t.work.intro}</p>
        </div>

        <article className="lp-feature-row" aria-labelledby="product-mbot" data-testid="mbot-feature">
          <div>
            <div className="lp-section-meta">
              <span className="lp-status" data-testid="product-status" data-status={mbot.status}>
                {mbotCopy.status}
              </span>
            </div>
            <h3 id="product-mbot">
              {mbotCopy.name}
              <span className="lp-title-dot">.</span>
            </h3>
            {mbotCopy.tagline && <p className="lp-feature-tagline">{mbotCopy.tagline}</p>}
            <p className="lp-body-copy">{mbotCopy.text}</p>
            <ul className="lp-product-facts">
              {t.work.mbot.features.map((feature) => (
                <li key={feature}>{feature}</li>
              ))}
            </ul>
            <div className="lp-feature-actions">
              <a className="lp-button" href={mbotHref} target="_blank" rel="noopener noreferrer">
                <span>{t.work.mbot.cta}</span>
                <span aria-hidden="true">↗</span>
              </a>
              {mbot.playStoreUrl && (
                <a href={mbot.playStoreUrl} target="_blank" rel="noopener noreferrer">
                  <img
                    src={`/badges/google-play-badge-${language}.png`}
                    alt={t.googlePlayAlt}
                    width={114}
                    height={44}
                    loading="lazy"
                  />
                </a>
              )}
            </div>
          </div>
          <div className="lp-feature-media">
            <img className="lp-classroom-photo" src="/mbot-kids-1.jpg" alt={t.work.mbot.imageAlt} width={1920} height={1080} loading="lazy" />
            <div className="lp-mbot-example" role="group" aria-label={t.work.mbot.exampleLabel}>
              <p className="lp-mbot-question">{t.work.mbot.exampleQuestion}</p>
              <p className="lp-mbot-answer">{t.work.mbot.exampleAnswer}</p>
              <a className="lp-text-link" href="/mbot-conversation.png" target="_blank" rel="noopener noreferrer">
                {t.work.mbot.viewExample} <span aria-hidden="true">↗</span>
              </a>
            </div>
          </div>
        </article>

        <div className="lp-project-list" data-testid="project-list">
          {others.map((product) => {
            const copy = t.products.items[product.id];
            const href = productUrl(product, language, 'homepage_featured');
            const content = (
              <>
                <div className="lp-project-top">
                  <div className="lp-project-heading">
                    <h3>{copy.name}</h3>
                    {copy.institutions && (
                      <div className="lp-project-logos">
                        {copy.institutions.map((institution) => (
                          <img key={institution.name} src={institution.logo} alt={institution.name} loading="lazy" />
                        ))}
                      </div>
                    )}
                  </div>
                  <span className="lp-project-status" data-testid="product-status" data-status={product.status}>
                    {copy.status}
                  </span>
                </div>
                <p>{copy.text}</p>
                <span className="lp-project-arrow" aria-hidden="true">
                  {href ? '↗' : ''}
                </span>
              </>
            );

            return href ? (
              <a
                key={product.id}
                className="lp-project"
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`project-${product.id}`}
              >
                {content}
              </a>
            ) : (
              <article key={product.id} className="lp-project" data-testid={`project-${product.id}`}>
                {content}
              </article>
            );
          })}
        </div>

      </div>
    </section>
  );
};

export default WorkSection;
